import React, { useEffect, useState } from "react"
import { MobergColumn, MobergDropdown, MobergInputLabel, MobergNumberInput, MobergRow, MobergFontFamily, MobergCheckbox } from "../../../../../../../Moberg"
import { Analytic, COxAnalysis } from "../../../Types/AnalysisDetails"
import { DurationUnit, fromMilliseconds, getDurationUnitRange, toMilliseconds, useDuration } from "../../../Hooks/useDuration"
import { useTraceOptions } from "./useTraceOptions"
import { equalsTraceOptionOrString } from "../../../utils"

type EditCOxProps = {
	initialState: COxAnalysis
	onChange: (analysis: COxAnalysis) => void
}

export const EditCOx: React.FC<EditCOxProps> = ({ initialState, onChange }) => {
	const traceOptions = useTraceOptions()
	const [enableLeft, setEnableLeft] = useState<boolean>(initialState.enableLeft)
	const [enableRight, setEnableRight] = useState<boolean>(initialState.enableRight)
	const [StO2Left, setStO2Left] = useState<string>(initialState.StO2Left)
	const [StO2Right, setStO2Right] = useState<string>(initialState.StO2Right)
	const [ABP, setABPModality] = useState<string>(initialState.ABP)

	const [calculationWindow, updateCalculationWindow] = useDuration(fromMilliseconds(initialState.calculationWindowMs, getDurationUnitRange(DurationUnit.SECONDS, DurationUnit.HOURS)))
	const [calculationPeriod, updateCalculationPeriod] = useDuration(fromMilliseconds(initialState.calculationPeriodMs, getDurationUnitRange(DurationUnit.SECONDS, DurationUnit.MINUTES)))
	const [validationErrors, setValidationErrors] = useState(new Map<string, string | undefined>())

	const formIsInvalid = Object.values(validationErrors).filter(err => err !== undefined).length > 0 || isNaN(calculationPeriod.value) || isNaN(calculationWindow.value)

	const validateDurationValue = (value: number, units: DurationUnit): string | undefined => {
		if (value <= 0) {
			return "The duration must be greater than 0."
		}

		if (toMilliseconds(value, units) < 10 * 1000) {
			return "The duration cannot be less than 10 seconds"
		}

		return undefined
	}

	const updateValidationError = (key: string, error: string | undefined) => {
		setValidationErrors(previous => {
			const next = new Map(previous)
			next.set(key, error)
			return next
		})
	}

	useEffect(() => {
		if (formIsInvalid) {
			return
		}

		onChange({
			analytic: Analytic.COx,
			StO2Left,
			StO2Right,
			enableLeft,
			enableRight,
			ABP,
			calculationPeriodMs: calculationPeriod.milliseconds,
			calculationWindowMs: calculationWindow.milliseconds,
		})
	})

	return (
		<MobergColumn gap="16px">
			<div style={{ display: 'flex', alignItems: 'center', marginLeft: 2, fontSize: 12, color: '#444', fontFamily: MobergFontFamily.REGULAR }}>
				<span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#207DEA', marginRight: 8 }} />
				<span>Blue dot denotes available data source in the selected patient file</span>
			</div>
			
			<MobergColumn gap="8px">
				<MobergInputLabel text={"Calculation Window"} />
				<MobergRow gap="16px">
					<div style={{ width: "120px" }}>
						<MobergNumberInput
							defaultValue={initialState.calculationWindowMs}
							value={calculationWindow.value}
							onChange={newValue => updateCalculationWindow(newValue ?? 10, calculationWindow.units)}
							validationFunction={value => validateDurationValue(value, calculationWindow.units)}
							onValidationErrorChange={error => updateValidationError("calculation window", error)}
							style={{ width: "112px" }}
						/>
					</div>

					<MobergDropdown
						options={getDurationUnitRange(DurationUnit.SECONDS, DurationUnit.HOURS).map((unit: DurationUnit) => ({ label: unit, value: unit }))}
						onChange={newUnits => updateCalculationWindow(calculationWindow.value, newUnits)}
						selectedValue={calculationWindow.units}
					/>
				</MobergRow>
			</MobergColumn>

			<MobergColumn gap="8px">
				<MobergInputLabel text={"Calculate every"} />
				<MobergRow gap="16px">
					<div style={{ width: "120px" }}>
						<MobergNumberInput
							defaultValue={initialState.calculationPeriodMs}
							value={calculationPeriod.value}
							onChange={newValue => updateCalculationPeriod(newValue ?? 10, calculationPeriod.units)}
							validationFunction={value => validateDurationValue(value, calculationPeriod.units)}
							onValidationErrorChange={error => updateValidationError("calculation period", error)}
							style={{ width: "112px" }}
						/>
					</div>

					<MobergDropdown
						options={getDurationUnitRange(DurationUnit.SECONDS, DurationUnit.MINUTES).map((unit: DurationUnit) => ({ label: unit, value: unit }))}
						onChange={newUnits => updateCalculationPeriod(calculationPeriod.value, newUnits)}
						selectedValue={calculationPeriod.units}
					/>
				</MobergRow>
			</MobergColumn>

		
			<MobergColumn gap="8px">
				<MobergInputLabel text={"ABP"} />

				<MobergDropdown
					options={traceOptions}
					onChange={newValue => setABPModality(newValue.rawDataKey)}
					selectedValue={ABP}
					equals={equalsTraceOptionOrString}
				/>
			</MobergColumn>

			<MobergRow gap="32px">
				<MobergColumn gap="8px">
					<MobergInputLabel text={"StO2 (left)"} />

					<MobergDropdown
						options={traceOptions}
						onChange={newValue => setStO2Left(newValue.rawDataKey)}
						selectedValue={StO2Left}
						equals={equalsTraceOptionOrString}
					/>
				</MobergColumn>

				<MobergColumn gap="8px">
					<MobergInputLabel text={"Enabled"} />
					<MobergCheckbox isChecked={enableLeft} onChange={setEnableLeft} />
				</MobergColumn>
			</MobergRow>

			<MobergRow gap="32px">
				<MobergColumn gap="8px">
					<MobergInputLabel text={"StO2 (right)"} />

					<MobergDropdown
						options={traceOptions}
						onChange={newValue => setStO2Right(newValue.rawDataKey)}
						selectedValue={StO2Right}
						equals={equalsTraceOptionOrString}
					/>
				</MobergColumn>

				<MobergColumn gap="8px">
					<MobergInputLabel text={"Enabled"} />
					<MobergCheckbox isChecked={enableRight} onChange={setEnableRight} />
				</MobergColumn>
			</MobergRow>
		</MobergColumn>
	)
}
